
import { useNavigate } from '@/lib/router-dom';
import api from '@/services/api/messages.js';
import classname from 'classnames';
import { produce } from 'immer';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useConfig from '../use-config/index';
import useMessages from '../use-messages/index';
import { useFeature } from '@/hooks/use-feature/index'
import { features as featureConstant } from "@/utils/feature-verifier";

const useDataPage = (props) => {
    const { parentComponentName, initialSortConfig } = props ?? {}
    const { goTo } = useNavigate();

    const isAgentPrincipalUIEnabled = useFeature(featureConstant.AGENT_PRINCIPAL_UI)

    const [fileType, setFileType] = useState()
    const [fileSize, setFileSize] = useState(0)
    const [includeAttachments, setIncludeAttachments] = useState(false)
    const [includeAttachmentsError, setIncludeAttachmentsError] = useState(false)

    const [dataElementsOptions, setDataElementsOptions] = useState([])
    const [participantsOptions, setParticipantsOptions] = useState([])

    const [statusOptions, setStatusOptions] = useState([
        { id: 'PENDING', label: "Pending", $selected: false },
        { id: 'SUCCESS', label: "Success", $selected: false },
        { id: 'EXPIRED', label: "Expired", $selected: false },
        { id: 'FAIL', label: "Failed", $selected: false },
    ])

    useEffect(() => {
        setStatusOptions([
            { id: 'PENDING', label: (parentComponentName === "received-data" ? "Receiving" : "Sending"), $selected: false },
            { id: 'SUCCESS', label: (parentComponentName === "received-data" ? "Received" : "Sent"), $selected: false },
            { id: 'EXPIRED', label: "Expired", $selected: false },
            { id: 'FAIL', label: "Failed", $selected: false },
            { id: 'PENDING_APPROVAL', label: "Pending Approval", $selected: false },
            { id: 'REJECTED', label: "Rejected", $selected: false },
        ])
    }, [])

    const [typeOptions, setTypeOptions] = useState([
        { id: 'PULL', label: "Pull", $selected: false },
        { id: 'RECEIVE', label: "Receive", $selected: false },
        ...(isAgentPrincipalUIEnabled ? [{ id: 'PULL_FROM_AGENT', label: "Pull from Agent", $selected: false }] : [])
    ])

    const [sortConfig, setSortConfig] = useState(
		initialSortConfig ?? { key: "created", direction: false }
	);

    const [currentPage, setCurrentPage] = useState(1)
    const { messagesMeta } = useMessages()

    const { isConsumer } = useConfig()
    const { storeDays, receiveDays } = useSelector(state => state.dataExpiry)

    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)

    const [error, setError] = useState()
    const [updatedData, setUpdatedData] = useState([])

    function handleSort(key, _type) {
        let direction = sortConfig.direction
        if (sortConfig.key === key) {
            direction = !direction
        } else {
            direction = true
        }

        const sortDirection = direction === true ? "asc" : "desc";

        setSortConfig({ key, direction })
    }

    function goToPrev() {

        if (currentPage === 1) {
            return
        }
        resetDataUpdate()
        setCurrentPage(prev => prev - 1)
    }

    function goToNext() {
        if (currentPage === messagesMeta?.totalPages) {
            return
        }
        resetDataUpdate()
        setCurrentPage(prev => prev + 1)
    }

    const goToRequest = () => {
        return goTo('/received-data/request')
    }

    function goToTransaction(messageId, type) {
        if (type === "consumer")
            goTo(`/received-data/transaction/${messageId}`, { type: type })
        if (type === "provider")
            goTo(`/shared-data/transaction/${messageId}`, { type: type })
    }

    const goToShare = () => {
        goTo('/shared-data/share', { type: "new" })
    }

    const goToStimulateProvide = () => {
        return goTo('/shared-data/simulate-provide')
    }

    const goToSimulateReceive = () => {
        return goTo('/received-data/simulate-receive')
    }

    let timeout

    function handleCurrentInput(e) {
        const value = e.target?.value
        const current = parseInt(value)

        clearTimeout(timeout)

        timeout = setTimeout(() => {
            if (current > messagesMeta?.totalPages) {
                return setCurrentPage(messagesMeta?.totalPages)
            }

            if (isNaN(current)) {
                setError("Invalid page number")
            }

            setCurrentPage(current)
        })
    }

    function isAllChecked(itemData) {
        const everySuccess = itemData.filter(field => field.status.toLowerCase() === "success")

        if (everySuccess.length > 0) {
            return everySuccess.every(field => field.$selected === true)
        }

        return false
    }

    function changeListCheckboxes(event, itemData) {
        const index = itemData.findIndex(field => field.id === parseInt(event.target.value))
        const updatedItem = produce(itemData, draft => {
            draft[index].$selected = !draft[index].$selected
        })
        setUpdatedData(updatedItem)
    }

    function changeAllListCheckboxes(event, itemsData) {
        if (event.target.checked) {
            const updatedItems = itemsData.map(field => field.status.toLowerCase() === "success" ? ({ ...field, $selected: true }) : ({ ...field }))
            return setUpdatedData(updatedItems)
        }

        const updatedItem = itemsData.map(field => field.status.toLowerCase() === "success" ? ({ ...field, $selected: false }) : ({ ...field }))
        setUpdatedData(updatedItem)
    }

    function getTypeName(type, input) {
        if (input === "RECIEVED") {
            if (type === "PULL") {
                return 'Pull'
            }
            if (type === "RECEIVE") {
                return 'Received'
            }
        }
        if (input === "SHARED") {
            if (type === "PUSH") {
                return 'Push'
            }
            if (type === "STORE") {
                return 'Store'
            }
            if (type === "PROVIDE") {
                return 'Provide'
            }
        }
    }

    function calculateRemainingDay(status, item) {
        if (status.toLowerCase() === "success") {
            let expireDate
            let expireCheck
            if (item.type === "STORE") {
                expireDate = moment(item.created).add(storeDays, "days")
                expireCheck = moment().add(storeDays, "days")
            } else {
                expireDate = moment(item.created).add(receiveDays, "days")
                expireCheck = moment().add(receiveDays, "days")
            }
            const today = moment()
            if (expireDate < today) {
                return 0
            }

            if (expireDate < expireCheck) {
                const diffDate = moment(expireDate).diff(today, "days")

                if (diffDate === 0) {
                    return 1
                }

                return diffDate
            }

            return 0
        }

        return 0
    }

    function convertArrayToString(array) {
        if (!Array.isArray(array)) {
            return null
        }

        if (array.every(item => item.$selected === true)) {
            return null
        }

        const tempArray = retrieveIdfromMessages(array)

        if (tempArray.length > 0) {
            return tempArray.toString();
        }

        return null
    }

    function retrieveIdfromMessages(array) {
        const tempArray = [];

        array.forEach(item => {
            if (item.$selected) {
                tempArray.push(item.id)
            }
        })

        return tempArray
    }

    async function checkFileSize(file) {
        const tempArray = retrieveIdfromMessages(file.filter(message => message.$selected === true));

        const [error, data] = await api.checkFileSize({
            id: tempArray
        })

        if (error) {
            return setError(error?.error?.message || "Failed to check file size")
        }
        return setFileSize(data.totalFileSize)
    }

    async function downloadFile(file) {
        const tempArray = retrieveIdfromMessages(file.filter(message => message.$selected === true));
        const [error, result] = await api.downloadFile({
            id: tempArray,
        }, fileType, {
            params: {
                "includeAttachments": includeAttachments
            }
        })

        if (error) {
            return setError(error?.error?.message || "Failed to download file(s)")
        }

        const link = document.createElement("a")
        if (!includeAttachments && tempArray.length === 1) {
            const fileName = file.filter(message => message.$selected === true)[0]?.dataElement.id;
            if (fileType === "json") {
                const path = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(result))}`
                link.href = path;
                link.download = `${fileName + ".json"}`;
                link.click();
                return
            }
            if (fileType === "csv") {
                const path = `data:text/csv;charset=utf-8,%EF%BB%BF${result}`
                link.href = path;
                link.download = `${fileName + ".csv"}`;
                link.click();
                return
            }
        }

        const path = `data:application/zip;base64,${result}`
        link.href = path;
        link.download = "attachment.zip";
        link.click();
    }

    const getOverviewClass = classname({
        "overview": true,
        "overview--empty": !isConsumer
    })

    function resetDataUpdate() {
        setUpdatedData([])
    }

    function isAnySelected(array) {
        if (!Array.isArray(array)) {
            return false
        }
        return array.some(item => item.$selected);
    }

    return {
        fileType,
        setFileType,
        fileSize,
        setFileSize,
        includeAttachments,
        setIncludeAttachments,
        includeAttachmentsError,
        setIncludeAttachmentsError,
        dataElementsOptions,
        setDataElementsOptions,
        participantsOptions,
        setParticipantsOptions,
        statusOptions,
        setStatusOptions,
        typeOptions,
        setTypeOptions,
        handleSort,
        sortConfig,
        setSortConfig,
        goToPrev,
        goToNext,
        currentPage,
        setCurrentPage,
        goToRequest,
        goToTransaction,
        goToShare,
        goToStimulateProvide,
        handleCurrentInput,
        isAllChecked,
        changeListCheckboxes,
        changeAllListCheckboxes,
        getTypeName,
        calculateRemainingDay,
        convertArrayToString,
        retrieveIdfromMessages,
        checkFileSize,
        downloadFile,
        getOverviewClass,
        goToSimulateReceive,
        updatedData,
        isAnySelected
    }

}

export default useDataPage
